import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { onAuthStateChanged, getAuth, User } from "firebase/auth";
import {
  FaReact,
  FaNodeJs,
  FaPython,
  FaDatabase,
  FaDocker,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import {
  SiJavascript,
  SiTypescript,
  SiFlutter,
  SiAngular,
  SiVuedotjs,
  SiKotlin,
  SiSwift,
  SiCplusplus,
  SiSpringboot,
  SiOpenai,
  SiDjango,
} from "react-icons/si";
import { db } from "../firebase";
import { ScrollAnimation, StaggerContainer, StaggerItem, ParallaxScroll } from "./ScrollAnimation";

const iconOptions = [
  { label: "JavaScript", value: "SiJavascript", icon: <SiJavascript /> },
  { label: "React", value: "FaReact", icon: <FaReact /> },
  { label: "TypeScript", value: "SiTypescript", icon: <SiTypescript /> },
  { label: "Node.js", value: "FaNodeJs", icon: <FaNodeJs /> },
  { label: "Flutter", value: "SiFlutter", icon: <SiFlutter /> },
  { label: "Python", value: "FaPython", icon: <FaPython /> },
  { label: "Django", value: "SiDjango", icon: <SiDjango /> },
  { label: "Database", value: "FaDatabase", icon: <FaDatabase /> },
  { label: "Spring Boot", value: "SiSpringboot", icon: <SiSpringboot /> },
  { label: "Docker", value: "FaDocker", icon: <FaDocker /> },
  { label: "Angular", value: "SiAngular", icon: <SiAngular /> },
  { label: "Vue.js", value: "SiVuedotjs", icon: <SiVuedotjs /> },
  { label: "Kotlin", value: "SiKotlin", icon: <SiKotlin /> },
  { label: "Swift", value: "SiSwift", icon: <SiSwift /> },
  { label: "C++", value: "SiCplusplus", icon: <SiCplusplus /> },
  { label: "LLM", value: "SiOpenai", icon: <SiOpenai /> },
];

interface Skill {
  id: string;
  name: string;
  level: number;
  icon: string;
}

// Circular progress ring component
const CircularProgress: React.FC<{ level: number; index: number }> = ({ level, index }) => {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative w-16 h-16">
      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
        {/* Background ring */}
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="text-gray-100 dark:text-surface-800"
        />
        {/* Progress ring */}
        <motion.circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="url(#skillGradient)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          whileInView={{ strokeDashoffset: circumference - (level / 100) * circumference }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut", delay: index * 0.05 }}
        />
        <defs>
          <linearGradient id="skillGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{level}%</span>
      </div>
    </div>
  );
};

const SkillsSection: React.FC = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Skill>({
    id: "",
    name: "",
    level: 0,
    icon: "SiJavascript",
  });
  const [user, setUser] = useState<User | null>(null);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const skillsSnapshot = await getDocs(collection(db, "skills"));
      setSkills(
        skillsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Skill[]
      );
    } catch (error) {
      console.error("Error fetching skills:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { id, name, level, icon } = formData;
    try {
      if (id) {
        const docRef = doc(db, "skills", id);
        await updateDoc(docRef, { name, level, icon });
      } else {
        await addDoc(collection(db, "skills"), { name, level, icon });
      }
      fetchSkills();
      setFormData({ id: "", name: "", level: 0, icon: "SiJavascript" });
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving skill:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const docRef = doc(db, "skills", id);
      await deleteDoc(docRef);
      fetchSkills();
    } catch (error) {
      console.error("Error deleting skill:", error);
    }
  };

  const getIconComponent = (iconName: string) => {
    const icon = iconOptions.find((opt) => opt.value === iconName);
    return icon ? icon.icon : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40">
        <div className="w-10 h-10 border-4 border-primary-200 dark:border-primary-900 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <section className="section-padding relative" id="skills">
      {/* Background decoration with parallax */}
      <ParallaxScroll offset={30}>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      </ParallaxScroll>

      <div className="max-w-6xl mx-auto relative">
        {/* Section Header with enhanced animation */}
        <ScrollAnimation variant="slideUp" delay={0.1} duration={0.7}>
          <div className="text-center mb-16">
            <motion.div
              className="section-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Technical Expertise
            </motion.div>
            <h2 className="section-title">
              My <span className="gradient-text">Skills</span>
            </h2>
            <p className="section-subtitle">
              Technologies and tools I work with to bring ideas to life
            </p>
          </div>
        </ScrollAnimation>

        {/* Skills Grid with stagger animation */}
        <StaggerContainer staggerDelay={0.08}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {skills.map((skill) => (
              <StaggerItem key={skill.id}>
                <motion.div
                  className="glass-card p-6 text-center group relative overflow-hidden"
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  {/* Hover glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/0 to-accent-500/0 group-hover:from-primary-500/5 group-hover:to-accent-500/5 transition-all duration-500" />

                  {/* Circular Progress */}
                  <div className="relative mx-auto mb-4 flex justify-center">
                    <CircularProgress level={skill.level} index={0} />
                  </div>

                  {/* Icon */}
                  <motion.div
                    className="text-2xl text-primary-600 dark:text-primary-400 mb-3 flex justify-center"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {getIconComponent(skill.icon)}
                  </motion.div>

                  {/* Name */}
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                    {skill.name}
                  </h3>

                  {/* Edit/Delete - Mobile-friendly */}
                  {user && (
                    <div className="absolute top-2 right-2 z-20">
                      <div className="admin-controls">
                        <button
                          className="btn-icon-sm"
                          onClick={() => {
                            setFormData(skill);
                            setIsEditing(true);
                          }}
                          aria-label={`Edit ${skill.name} skill`}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn-icon-sm btn-danger"
                          onClick={() => handleDelete(skill.id)}
                          aria-label={`Delete ${skill.name} skill`}
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              </StaggerItem>
            ))}
          </div>
        </StaggerContainer>

        {/* Add Skill Button */}
        {user && (
          <motion.div className="flex justify-center mt-10">
            <motion.button
              className="btn-primary !py-2.5 !px-5 !text-sm"
              onClick={() => setIsEditing(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Add Skill
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Modal */}
      {isEditing && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => {
            setFormData({ id: "", name: "", level: 0, icon: "SiJavascript" });
            setIsEditing(false);
          }}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              {formData.id ? "Edit Skill" : "Add Skill"}
            </h3>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Skill Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. React"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Proficiency (0-100)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.level}
                  onChange={(e) => setFormData((prev) => ({ ...prev, level: +e.target.value }))}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Icon
                </label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
                  className="input-field"
                >
                  {iconOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="btn-secondary !py-2.5 !px-5 !text-sm"
                  onClick={() => {
                    setFormData({ id: "", name: "", level: 0, icon: "SiJavascript" });
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary !py-2.5 !px-5 !text-sm">
                  Save
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default SkillsSection;
